import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import {
  clearFuelDashboard,
  clearFuelData,
  clearFuelLosses,
  clearTanks,
  getFuelAlerts,
  getFuelConsumptionOverTime,
  getFuelDashboardTanks,
  getFuelLosses,
  getFuelTanks,
  getLastBatches,
  getLastDispenses,
} from 'features/fuelManagement/fuelManagementSlice'
import {
  getFuelCustodyPricing,
} from 'features/fuelManagement/fuelCustodySlice'
import {
  clearFuelConsumption,
  getConsumptionMachines,
} from 'features/fuelManagement/fuelConsumptionSlice'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Flex, MainHeadline, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { IBatch } from 'types/api/fuelManagementApi.interface'
import { formatDateForDisplay, formatNumbersForDisplay } from 'utils/helpers'
import { AddBatchModal } from './AddBatchModal'
import {
  ChartTick,
  ChartTickRow,
  DispenseLogFootnote,
  FuelAlarmBanner,
  FuelGrid,
  FuelKpiStrip,
  FuelPageSubtitle,
  FuelWidget,
  HeaderLink,
  MachineRow,
  MachineRowMeta,
  MachineRowName,
  MachineRowRate,
  MachineRowText,
  TankBarScale,
  TankRow,
  TankRowCover,
  TankRowMeta,
  TankRowName,
  TankRowPct,
  TankRowText,
  WidgetBody,
  WidgetLoader,
} from './FuelManagement.styled'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { createPortal } from 'react-dom'
import { AreaChart } from 'components/Chart/AreaChart'
import useQueryPagination from 'hooks/useQueryPagination'
import { fuelConsumptionPeriods, fuelAlertRiskLevels } from './fuelData'
import { parseISO, format, isValid } from 'date-fns'
import { FuelAlertsModal } from './FuelAlertsModal'
import { useNavigate } from 'react-router-dom'
import { TankLevelBar } from './Tanks/TankLevelBar'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { AlertListRow } from 'components/ui/AlertListRow/AlertListRow'
import { AlertList } from 'components/ui/AlertListRow/AlertListRow.styled'
import { tokens } from 'styling/tokens'
import { Button } from 'components/ui/Button/Button'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { AlarmContent, AlarmDot, AlarmText } from 'pages/Operations/Safety/Safety.styled'
import { MachineFuelLink } from './Machines/MachineFuelLink'
import { IFuelOpsDispenseRow } from 'types/fuelOps.interface'
import { IFuelTankRowVM } from 'types/fuelReconciliation.interface'
import { FuelTankClass } from 'types/fuelEnums.interface'
import { TANK_CLASS_TO_UI } from './Shared/fuelEnumMaps'
import {
  criticalLossBannerText,
  findOpenCriticalLoss,
  formatGhs,
  formatGhsCompact,
  FUEL_DISPENSE_FOOTNOTE,
} from './Shared/fuelFormatters'
import {
  bandDeltaPct,
  bandStateOf,
  signedPctLabel,
  worstFuelMachines,
} from './Shared/fuelConsumptionUtils'
import { mapDispenseToRow, mapFuelLoss, mapFuelMachineLeague } from './Shared/fuelApiMappers'

const riskTone = (level: number): 'danger' | 'warning' | 'info' | 'neutral' =>
  level >= 3 ? 'danger' : level === 2 ? 'warning' : level === 1 ? 'info' : 'neutral'

const shortActionLabel = (label: string): string => {
  const words = label.split(' ')
  return words.length <= 2 ? label : `${words[0]} ${words[words.length - 1]}`
}

const BAND_COLOR = {
  over: tokens.status.danger.fg,
  in: tokens.status.success.fg,
  under: tokens.text.muted,
}

const TANK_TONE_RANK = { danger: 0, warning: 1, success: 2, neutral: 3, info: 3, accent: 3 }

const toTankRow = (tank: any, wacPerLitre: number, coverDangerDays: number, coverWarningDays: number): IFuelTankRowVM => {
  const machineClass = tank.machineClass === FuelTankClass.Bowser ? 'BOWSER' : 'STATIC'
  const pct = tank.capacity > 0 ? Math.round((tank.currentAmount / tank.capacity) * 100) : 0
  const sensorAmount = tank.currentSensorAmount ?? null
  const daysOfCover = null
  const overfull = pct >= 100
  let tone: IFuelTankRowVM['tone'] = 'success'
  let statusLabel = 'Ok'
  if (machineClass === 'BOWSER') {
    tone = 'info'
    statusLabel = 'Bowser'
  } else if (overfull) {
    tone = 'warning'
    statusLabel = 'Overfull — verify dip & meter'
  } else if (daysOfCover != null && daysOfCover < coverDangerDays) {
    tone = 'danger'
    statusLabel = 'Reorder now'
  } else if (daysOfCover != null && daysOfCover < coverWarningDays) {
    tone = 'warning'
    statusLabel = 'Watch'
  }
  return {
    id: tank.id,
    name: tank.name,
    currentAmount: tank.currentAmount,
    capacity: tank.capacity,
    pct,
    stockValue: tank.currentAmount * wacPerLitre,
    burnRatePerDay: 0,
    daysOfCover,
    tone,
    statusLabel,
    reorderLitres: Math.max(tank.capacity - tank.currentAmount, 0),
    overfull,
    sensorAmount,
    sensorPct: sensorAmount != null && tank.capacity > 0 ? Math.round((sensorAmount / tank.capacity) * 100) : null,
    source: tank,
  }
}

const fmtCover = (days: number | null): string => {
  if (days == null) return '—'
  return Number.isInteger(days) ? `${days} days` : `${days.toFixed(1)} days`
}

const coverColor = (vm: IFuelTankRowVM): string =>
  vm.tone === 'danger' || vm.tone === 'warning' ? tokens.status[vm.tone].fg : tokens.text.muted

const dispenseColumns: IHonestTableColumn<IFuelOpsDispenseRow>[] = [
  { id: 'day', header: 'DAY', cell: (item) => item.dayLabel },
  { id: 'time', header: 'TIME', cell: (item) => item.time },
  { id: 'machine', header: 'MACHINE', cell: (item) => <MachineFuelLink name={item.machine} machineId={item.machineId} /> },
  { id: 'operator', header: 'OPERATOR', cell: (item) => item.operator },
  { id: 'liters', header: 'LITRES', cell: (item) => <BodyText weight='bold'>{formatNumbersForDisplay(item.liters)} L</BodyText> },
  { id: 'smu', header: 'SMU AT FILL', cell: (item) => (item.smuHours != null ? formatNumbersForDisplay(item.smuHours) + ' h' : '—') },
  { id: 'tank', header: 'SOURCE', cell: (item) => item.tank },
  {
    id: 'flag',
    header: 'FLAG',
    minWidth: '14rem',
    cell: (item) => (item.flag
      ? <StatusPill status={item.flag.tone} dot={item.flag.tone === 'danger'}>{item.flag.label}</StatusPill>
      : <OtherText grey>—</OtherText>),
  },
]

const batchColumns: IHonestTableColumn<IBatch>[] = [
  { id: 'amount', header: 'Amount', cell: (item) => <BodyText weight='bold'>{formatNumbersForDisplay(item.quantity) || '/'}</BodyText> },
  { id: 'tank', header: 'Fuel Tank', cell: (item) => item?.fuelTank?.name || '/' },
  { id: 'fuelType', header: 'Fuel Type', cell: (item) => item?.fuelType.name || '/' },
  { id: 'supplier', header: 'Supplier', cell: (item) => item?.supplier?.name || '/' },
  { id: 'date', header: 'Date of purchase', cell: (item) => formatDateForDisplay(item.date, true) },
]

export const FuelManagement = () => {
  const {
    batches,
    dispenses,
    dashboardTanks,
    fuelConsumption,
    alerts,
    isLoadingAlerts,
    tanks,
    fuelLosses,
  } = useSelector((state: RootState) => state.fuelManagement)
  const { pricing } = useSelector((state: RootState) => state.fuelCustody)
  const { machines: consumptionMachines } = useSelector((state: RootState) => state.fuelConsumption)
  const [batchesModal, setBatchesModal] = useState(false)
  const [notificationsModal, setNotificationsModal] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState(1)
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  useQueryPagination({
    fetchFunctions: [getFuelConsumptionOverTime, getLastBatches, getLastDispenses, getFuelDashboardTanks, getFuelAlerts],
    cleanUpFunctions: [clearFuelData, clearFuelDashboard],
    params: null,
    runInParallel: true,
  })

  useEffect(() => {
    dispatch(getFuelTanks({ page: 1, limit: 5 }))
    dispatch(getFuelCustodyPricing())
    dispatch(getConsumptionMachines({ page: 1, limit: 20 }))
    dispatch(getFuelLosses({ page: 1, limit: 50 }))
    return () => {
      dispatch(clearTanks())
      dispatch(clearFuelConsumption())
      dispatch(clearFuelLosses())
    }
  }, [dispatch])

  const data = useMemo(() => {
    if (!fuelConsumption?.points?.length) return []
    return fuelConsumption.points.map(({ groupByKey, liters }) => {
      const date = parseISO(groupByKey)
      const safeValue = Number(liters) || 0
      let name = ''
      switch (selectedPeriod) {
        case 1:
          name = isValid(date) ? format(date, 'MMM') : groupByKey
          break
        case 2:
          name = isValid(date) ? format(date, 'd') : groupByKey
          break
        case 3:
          name = isValid(date) ? format(date, 'EEE') : groupByKey
          break
        case 4:
          name = isValid(date) ? format(date, 'H') : groupByKey
          break
        default:
          name = groupByKey
      }
      return { name, value: safeValue }
    })
  }, [fuelConsumption, selectedPeriod])

  const wacPerLitre = pricing?.wacPerLitre ?? 0
  const stockLiters = pricing?.stockLitres ?? dashboardTanks?.totalTankCurrentAmount ?? 0
  const stockValueGhs = pricing?.stockValueGhs ?? Math.round(stockLiters * wacPerLitre)

  const lossRows = useMemo(
    () => (fuelLosses?.data?.length ? fuelLosses.data.map(mapFuelLoss) : []),
    [fuelLosses],
  )
  const criticalLoss = findOpenCriticalLoss(lossRows)

  const chartData = data
  const tickStep = Math.max(1, Math.ceil(chartData.length / 7))
  const chartTicks = chartData.filter((_, index) => index % tickStep === 0)

  const tankRows: IFuelTankRowVM[] = useMemo(() => {
    const source: any[] = tanks?.data?.length ? tanks.data : dashboardTanks?.tanks ?? []
    return source
      .filter((t) => t.machineClass !== FuelTankClass.Bowser)
      .map((t) => toTankRow(t, wacPerLitre, 3, 7))
      .sort((a, b) =>
        TANK_TONE_RANK[a.tone] - TANK_TONE_RANK[b.tone] ||
        (a.daysOfCover ?? Infinity) - (b.daysOfCover ?? Infinity))
      .slice(0, 5)
  }, [tanks, dashboardTanks, wacPerLitre])

  const worstMachines = useMemo(() => {
    const mapped = consumptionMachines.data.map(mapFuelMachineLeague)
    return worstFuelMachines(mapped)
  }, [consumptionMachines.data])

  const dispenseRows: IFuelOpsDispenseRow[] = useMemo(
    () => (dispenses?.data?.length ? dispenses.data.map(mapDispenseToRow) : []),
    [dispenses],
  )

  const lastBatch = batches?.data?.[0]

  const handleConsumption = (period: number) => {
    setSelectedPeriod(period)
    dispatch(getFuelConsumptionOverTime(period))
  }

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center>
          <MainHeadline>Fuel Management</MainHeadline>
          <Button variant='primary' onClick={() => setBatchesModal(true)}>
            Add Batch
          </Button>
        </Flex>
        <FuelPageSubtitle>Stock, burn and dispense activity across site tanks</FuelPageSubtitle>
        {criticalLoss && (
          <FuelAlarmBanner>
            <AlarmContent>
              <AlarmDot />
              <AlarmText>{criticalLossBannerText(criticalLoss)}</AlarmText>
            </AlarmContent>
            <Button variant='danger' size='sm' onClick={() => navigate('/fuel/losses')}>
              View loss
            </Button>
          </FuelAlarmBanner>
        )}
        {pricing && (
          <FuelKpiStrip>
            <MetricCard
              value={`${formatNumbersForDisplay(stockLiters)} L`}
              label='STOCK ON SITE'
              sublabel={`${formatGhs(stockValueGhs)} · GH₵ ${wacPerLitre.toFixed(2)}/L wtd avg`}
              accentColor={tokens.accent.base}
            />
            <MetricCard
              value={`GH₵ ${pricing.lastDeliveryPricePerLitre.toFixed(2)}/L`}
              label='LAST DELIVERY PRICE'
              sublabel='Site weighted-average cost layer'
              accentColor={tokens.accent.base}
            />
            {lastBatch && (
              <MetricCard
                value={`${formatDateForDisplay(lastBatch.date)} · ${formatNumbersForDisplay(lastBatch.quantity)} L`}
                label='LAST DELIVERY'
                sublabel={`${formatGhs(lastBatch.cost)} · ${lastBatch.supplier?.name ?? '—'}`}
                accentColor={tokens.status.success.fg}
              />
            )}
          </FuelKpiStrip>
        )}
        <FuelGrid>
          <FuelWidget $span={4}>
            <ContainerHeader>
              <Flex width='100%' between center>
                <TitleText>Tanks</TitleText>
                <HeaderLink type='button' onClick={() => navigate('/fuel/tanks')}>View all ›</HeaderLink>
              </Flex>
            </ContainerHeader>
            <WidgetBody>
              {tanks?.isLoading ? (
                <Flex width='100%' height='100%' justcenter center><WidgetLoader /></Flex>
              ) : !tankRows.length ? (
                <Flex width='100%'><NoItems title='Fuel Tanks' /></Flex>
              ) : (
                tankRows.map((vm) => (
                  <TankRow key={vm.id}>
                    <TankBarScale>
                      <TankLevelBar
                        pct={vm.pct}
                        tone={vm.tone}
                        currentAmount={vm.currentAmount}
                        capacity={vm.capacity}
                        sensorPct={vm.sensorPct}
                        sensorAmount={vm.sensorAmount}
                      />
                    </TankBarScale>
                    <TankRowText>
                      <TankRowName>{vm.name}</TankRowName>
                      <TankRowMeta>{`${formatNumbersForDisplay(vm.currentAmount)} / ${formatNumbersForDisplay(vm.capacity)} L`}</TankRowMeta>
                    </TankRowText>
                    <TankRowPct>{`${vm.pct}%`}</TankRowPct>
                    <TankRowCover $color={coverColor(vm)}>{fmtCover(vm.daysOfCover)}</TankRowCover>
                  </TankRow>
                ))
              )}
            </WidgetBody>
          </FuelWidget>

          <FuelWidget $span={4}>
            <ContainerHeader>
              <Flex width='100%' between center>
                <TitleText>Consumption</TitleText>
                <HeaderLink type='button' onClick={() => navigate('/fuel/machines')}>View all ›</HeaderLink>
              </Flex>
            </ContainerHeader>
            <ContainerContent gap='0.75rem'>
              <TabsContainer>
                {fuelConsumptionPeriods.map((p) => (
                  <Tabs
                    type='button'
                    key={p.value}
                    active={selectedPeriod === p.value}
                    onClick={() => handleConsumption(p.value)}
                  >
                    {p.label}
                  </Tabs>
                ))}
              </TabsContainer>
              {!chartData.length || chartData.every((d) => !d.value) ? (
                <Flex height='150px' width='100%' center justcenter><OtherText>No data available</OtherText></Flex>
              ) : (
                <Flex column width='100%' gap='0.5rem'>
                  <AreaChart data={chartData} height={180} />
                  <ChartTickRow>
                    {chartTicks.map((tick, index) => <ChartTick key={`${tick.name}_${index}`}>{tick.name}</ChartTick>)}
                  </ChartTickRow>
                </Flex>
              )}
            </ContainerContent>
          </FuelWidget>

          <FuelWidget $span={4}>
            <ContainerHeader>
              <Flex width='100%' between center>
                <TitleText>Alerts</TitleText>
                <HeaderLink type='button' onClick={() => setNotificationsModal(true)}>View all ›</HeaderLink>
              </Flex>
            </ContainerHeader>
            <WidgetBody>
              {isLoadingAlerts ? (
                <Flex height='100%' width='100%' justcenter center><WidgetLoader /></Flex>
              ) : alerts.length ? (
                <AlertList>
                  {alerts.slice(0, 6).map((a) => {
                    const riskWord = fuelAlertRiskLevels[a.riskLevel]
                    const hasAction = a.actions?.length > 0
                    return (
                      <AlertListRow
                        key={a.id}
                        title={a.summary}
                        meta={a.machine?.name ? `${a.machine.name} · ${riskWord}` : riskWord}
                        pillLabel={hasAction ? shortActionLabel(a.actions[0]) : riskWord}
                        pillTone={riskTone(a.riskLevel)}
                        onPillClick={hasAction ? () => setNotificationsModal(true) : undefined}
                      />
                    )
                  })}
                </AlertList>
              ) : (
                <OtherText grey>No alerts this week</OtherText>
              )}
            </WidgetBody>
          </FuelWidget>

          {worstMachines.length > 0 && (
            <FuelWidget $span={4}>
              <ContainerHeader>
                <Flex width='100%' between center>
                  <TitleText>Machines to watch</TitleText>
                  <HeaderLink type='button' onClick={() => navigate('/fuel/machines')}>View all ›</HeaderLink>
                </Flex>
              </ContainerHeader>
              <WidgetBody>
                {worstMachines.map((m) => {
                  const headline = m.avgRateLph
                  const delta = bandDeltaPct(headline, m.nominalBurnRateLph)
                  const state = bandStateOf(headline, m.nominalBurnRateLph)
                  const topFlag = m.flags[0]
                  return (
                    <MachineRow key={m.id} onClick={() => navigate(`/fuel/machines/${m.id}`)}>
                      <MachineRowText>
                        <MachineRowName>{m.name}</MachineRowName>
                        <MachineRowMeta>{`nominal ${m.nominalBurnRateLph} L/h · ${m.model}`}</MachineRowMeta>
                      </MachineRowText>
                      <MachineRowRate $color={BAND_COLOR[state]}>
                        {`${headline} L/h · ${signedPctLabel(delta)}`}
                      </MachineRowRate>
                      {topFlag ? (
                        <StatusPill status={topFlag.tone} dot={topFlag.tone === 'danger'}>{topFlag.label}</StatusPill>
                      ) : (
                        <StatusPill status='success'>IN BAND</StatusPill>
                      )}
                    </MachineRow>
                  )
                })}
              </WidgetBody>
            </FuelWidget>
          )}

          <FuelWidget $span={worstMachines.length ? 8 : 6}>
            <ContainerHeader>
              <Flex width='100%' between center>
                <TitleText>Recent batches</TitleText>
                <HeaderLink type='button' onClick={() => navigate('/fuel/dashboard/batches')}>View all ›</HeaderLink>
              </Flex>
            </ContainerHeader>
            <ContainerContent>
              {batches?.data.length ? (
                <HonestTable columns={batchColumns} data={batches.data.slice(0, 5)} keyField='id' />
              ) : (
                <Flex width='100%' height='100%' center justcenter><NoItems title='Batches' /></Flex>
              )}
            </ContainerContent>
          </FuelWidget>

          <FuelWidget $span={worstMachines.length ? 12 : 6}>
            <ContainerHeader>
              <Flex width='100%' between center>
                <TitleText>Dispenses — yesterday & today</TitleText>
                <HeaderLink type='button' onClick={() => navigate('/fuel/dashboard/dispenses')}>View all ›</HeaderLink>
              </Flex>
            </ContainerHeader>
            <ContainerContent gap='1rem'>
              {dispenseRows.length ? (
                <HonestTable columns={dispenseColumns} data={dispenseRows.slice(0, 6)} keyField='id' />
              ) : (
                <NoItems title='Dispenses' />
              )}
              <DispenseLogFootnote>
                <OtherText color={tokens.text.muted}>{FUEL_DISPENSE_FOOTNOTE}</OtherText>
              </DispenseLogFootnote>
            </ContainerContent>
          </FuelWidget>
        </FuelGrid>
        {batchesModal && <AddBatchModal closeModal={() => setBatchesModal(false)} />}
        {notificationsModal && createPortal(<FuelAlertsModal closeModal={() => setNotificationsModal(false)} />, document.body)}
      </PageWrapper>
    </>
  )
}
